<template>
  <d-container
    fluid
    class="main-content-container px-4">
    <!-- Page Header -->
    <d-row
      no-gutters
      class="page-header py-4">
      <d-col
        col
        sm="6"
        class="text-center text-sm-left mb-4 mb-sm-0">
        <span class="text-uppercase page-subtitle">{{ $t('potentialCustomers.potentialCustomersLabel') }}</span>
        <h3 class="page-title">{{ $t('potentialCustomers.potentialCustomersList') }}</h3>
      </d-col>

      <d-col
        col
        sm="6"
        class="text-right">
        <d-button
          theme="success"
          class="ml-2 text-nowrap"
          :disabled="downloadInProgress"
          @click.prevent.stop="downloadPotentialCustomers">
          <div
            v-if="downloadInProgress"
            class="spinner-border spinner-border-sm"
            role="status"></div>
          <template v-if="!downloadInProgress">
            <i class="material-icons">cloud_download</i>
            {{ $t('customer.exportCustomers') }}
          </template>
        </d-button>
      </d-col>
    </d-row>

    <list-filters
      class="mb-3"
      :config="filtersConfig"
      :isListLoaded="loaded"
      :view-name="'potential-customers'"
      @init="handleFiltersInit"
      @search="handleFiltersSearch" />

    <d-card style="width: 100%;">
      <table class="table mt-0 mb-0 full-width">
        <thead class="bg-light">
          <tr>
            <th
              scope="col"
              class="border-0">{{ $t('ui.name') }}</th>
            <th
              scope="col"
              class="border-0">{{ $t('ui.emailAddress') }}</th>
            <th
              scope="col"
              class="border-0">{{ $t('customer.telephone') }}</th>
            <th
              scope="col"
              class="border-0">{{ $t('potentialCustomers.otherData') }}</th>
            <th
              scope="col"
              class="border-0">{{ $t('ui.lastContactAt') }}</th>
            <th
              scope="col"
              class="border-0">{{ $t('potentialCustomers.activityStartAt') }}</th>
            <th
              scope="col"
              class="border-0">{{ $t('ui.actions') }}</th>
          </tr>
        </thead>
        <tbody v-if="loaded">
          <tr
            v-for="(customer, index) of customers"
            :key="'customer-' + index">
            <td class="align-middle">
              {{ customer.name }}
            </td>
            <td class="align-middle">
              {{ customer.email }}
            </td>
            <td class="align-middle">
              {{ customer.phone }}
            </td>
            <td class="align-middle">
              <small>
                <template v-if="customer.tax_number">
                  {{ customer.tax_number }}<br>
                </template>

                <template v-if="customer.tax_number">
                  {{ customer.city }}<br>
                </template>

                <template v-if="customer.tax_number">
                  {{ customer.street }} {{ customer.building }}
                  <template v-if="customer.locale"> / {{ customer.locale }}</template><br>
                </template>

                <template v-if="customer.tax_number">
                  {{ customer.postcode }}<br>
                </template>
              </small>
            </td>
            <td class="align-middle">
              {{ customer.last_contact_at }}
            </td>
            <td class="align-middle">
              {{ customer.activity_start_at }}
            </td>

            <td
              class="align-middle"
              style="width: 150px;">
              <d-button
                theme="primary"
                @click.native="$router.push('/potencjalni-klienci/podglad/' + customer.id)"
                class="mr-2"
                :title="$t('ui.edit')">
                <i class="material-icons">visibility</i>
              </d-button>
              <d-button
                v-if="userIsAdmin"
                theme="danger"
                :disabled="removeInProgress"
                @click.native="removePotentialCustomer(customer.id)"
                :title="$t('ui.delete')">
                <i class="material-icons">delete</i>
              </d-button>
            </td>
          </tr>
          <tr v-if="customers.length === 0">
            <td
              colspan="7"
              class="text-center">
              {{ $t('potentialCustomers.noPotentialCustomersToDisplay') }}&hellip;
            </td>
          </tr>
        </tbody>
      </table>

      <div
        v-if="!loaded"
        class="pb-3 pt-3 text-center">
        {{ $t('ui.dataLoading') }}&hellip;
      </div>

      <d-alert
        v-if="loadError"
        show
        theme="warning">
        {{ $t('ui.dataLoadingFailed') }} <a
          href="javascript:window.location.reload();"
        class="alert-link">{{ $t('ui.refreshPage') }}</a> {{ $t('ui.toTryAgain') }}
      </d-alert>
    </d-card>

    <pagination
      :meta-data="paginationMetaData"
      :initial-page="currentPage" />
  </d-container>
</template>

<script>
import debounce from 'lodash.debounce';
import HasPagination from '@/mixins/HasPagination.vue';
import ListUtils from '@/utils/ListUtils.js';
import Pagination from '@/components/common/Pagination.vue';
import CalendarUtils from '@/utils/CalendarUtils.js';
import cloneDeep from 'lodash.clonedeep';

export default {
  name: 'potential-customers',
  mixins: [
    HasPagination
  ],
  components: {
    'pagination': Pagination
  },
  computed: {
    userIsAdmin () {
      return this.$store.getters.getUserType === 'admin';
    },
    filtersConfig () {
      return [
        {
          key: 'search',
          apiKey: 'search',
          label: this.$t('ui.search'),
          placeholder: this.$t('potentialCustomers.potentialCustomersSearchPlaceholder'),
          type: 'text',
          isBasic: true,
          defaultValue: '',
          maxWidth: '50%'
        },
        {
          key: 'last_contact',
          label: this.$t('ui.lastContactAt'),
          type: 'daterange',
          isBasic: true,
          defaultValue: { startDate: null, endDate: null },
          apiTransform: (val, where) => {
            if (val.startDate) {
              where['last_contact_from'] = CalendarUtils.formatDateForPHP(new Date(val.startDate));
            }
            if (val.endDate) {
              where['last_contact_to'] = CalendarUtils.formatDateForPHP(new Date(val.endDate));
            }
          }
        }
      ];
    }
  },
  data () {
    return {
      customers: [],
      downloadInProgress: false,
      loaded: false,
      loadError: false,
      removeInProgress: false,
      activeFilters: {},
      apiQueryPayload: {}
    };
  },
  mounted () {},
  methods: {
    handleFiltersInit (filters, apiPayload) {
      this.activeFilters = filters;
      this.apiQueryPayload = apiPayload;

      this.loadFilteredData();
    },
    handleFiltersSearch (activeFilters, apiPayload) {
      this.activeFilters = activeFilters;
      this.apiQueryPayload = apiPayload;
      this.$bus.$emit('pagination-reset');
      this.debouncedLoadFilteredData();
    },
    debouncedLoadFilteredData: debounce(function () {
      this.loadFilteredData();
    }, 250),
    loadFilteredData () {
      let where = cloneDeep(this.apiQueryPayload) || {};

      ListUtils.loadPaginatedItemsData(this, {
        endpoint: '/potential-customers/items?page=' + this.currentPage,
        listField: 'customers',
        params: {
          where
        }
      });
    },
    removePotentialCustomer (customerID) {
      this.removeInProgress = true;

      ListUtils.removeItem({
        $http: this.$http,
        endpointBase: '/potential-customers/delete/',
        id: customerID,
        successTitle: this.$t('potentialCustomers.deleteSuccessTitle'),
        successText: this.$t('potentialCustomers.deleteSuccessText'),
        confirmTitle: this.$t('potentialCustomers.deleteConfirmTitle'),
        confirmText: this.$t('ui.deleteConfirmText'),
        errorTitle: this.$t('ui.errorOccured'),
        errorText: this.$t('potentialCustomers.deleteErrorText'),
        successAction: () => {
          this.removeInProgress = false;
          this.loadFilteredData();
        },
        cancelAction: () => {
          this.removeInProgress = false;
        }
      });
    },
    downloadPotentialCustomers () {
      if (this.downloadInProgress) {
        return;
      }

      this.downloadInProgress = true;

      this.$http.post('/potential-customers/export', {}, {
        responseType: 'blob'
      }).then((response) => {
        let link = document.createElement('a');
        link.href = URL.createObjectURL(new Blob([response.data]));
        link.setAttribute('download', this.$t('potentialCustomers.exportFilename') + '.xlsx');
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        this.downloadInProgress = false;
      }).catch(() => {
        this.downloadInProgress = false;
      });
    }
  }
};
</script>
